import { CLASS_NAMES, SELECTORS, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Impact Module Caption Toggle.
 * Supports multiple image cards with captions within one module.
 */
export default class ImpactModule extends MessageBus {
  /**
   * Constructor function of the ImpactModule class. Must receive one argument, a DOM
   * node representing the module's container.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node that is passed into the constructor
     */
    this.element = element;

    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {ImpactModule} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them.
   * Uses querySelectorAll so every caption in the module is captured,
   * not just the first one.
   * @returns {ImpactModule} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    // querySelectorAll returns a NodeList of ALL matching elements
    this.captions = Array.from(
      this.element.querySelectorAll(`.${SELECTORS.IMPACT_MODULE_CAPTION}`)
    );

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {ImpactModule} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleOutsideClick = this.onOutsideClick.bind(this);

    return this;
  }

  /**
   * Create event listeners for every caption toggle found in the module.
   * @returns {ImpactModule} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.captions.forEach((caption) => {
      const toggle = caption.querySelector(
        `.${SELECTORS.IMPACT_MODULE_CAPTION_TOGGLE}`
      );

      if (toggle) {
        toggle.addEventListener(EVENTS.CLICK, (e) =>
          this.onCaptionToggle(e, caption)
        );
      }
    });

    return this;
  }

  /**
   * Handles clicks outside any open caption — closes it.
   * @param {MouseEvent} e A reference to the click event object
   * @returns {ImpactModule} A reference to the current instance of the class
   */
  onOutsideClick(e) {
    this.captions.forEach((caption) => {
      if (
        caption.classList.contains(CLASS_NAMES.IS_EXPANDED) &&
        !caption.contains(e.target)
      ) {
        this.closeCaption(caption);
      }
    });

    return this;
  }

  /**
   * Handles caption toggle click — animates open or close.
   * @param {MouseEvent} e A reference to the click event object
   * @param {HTMLElement} caption The caption element associated with the clicked toggle
   * @returns {ImpactModule} A reference to the current instance of the class
   */
  onCaptionToggle(e, caption) {
    e.stopPropagation();

    const isExpanded = caption.classList.contains(CLASS_NAMES.IS_EXPANDED);

    if (!isExpanded) {
      this.openCaption(caption);
    } else {
      this.closeCaption(caption);
    }

    return this;
  }

  /**
   * Measures natural dimensions and animates the given caption open.
   * @param {HTMLElement} captionParam The caption element to open
   * @returns {ImpactModule} A reference to the current instance of the class
   */
  openCaption(captionParam) {
    const caption = captionParam;

    caption.style.transition = 'none';
    caption.style.width = '281px';
    caption.style.height = 'auto';

    const naturalHeight = caption.scrollHeight;

    caption.style.width = '44px';
    caption.style.height = '44px';

    // Force reflow
    caption.getBoundingClientRect();

    caption.style.transition = '';
    caption.style.width = '281px';
    caption.style.height = `${naturalHeight}px`;
    caption.classList.add(CLASS_NAMES.IS_EXPANDED);
    caption.setAttribute(SELECTORS.ARIA_EXPANDED, 'true');

    document.addEventListener(EVENTS.CLICK, this.handleOutsideClick);

    return this;
  }

  /**
   * Pins current dimensions and animates the given caption closed.
   * @param {HTMLElement} captionsParam The caption element to close
   * @returns {ImpactModule} A reference to the current instance of the class
   */
  closeCaption(captionsParam) {
    const captions = captionsParam;

    if (!captions.classList.contains(CLASS_NAMES.IS_EXPANDED)) return this;

    document.removeEventListener(EVENTS.CLICK, this.handleOutsideClick);

    captions.style.width = `${captions.scrollWidth}px`;
    captions.style.height = `${captions.scrollHeight}px`;

    // Force reflow
    captions.getBoundingClientRect();

    captions.style.width = '44px';
    captions.style.height = '44px';

    const onTransitionEnd = (e) => {
      if (e.propertyName !== 'width') return;
      captions.classList.remove(CLASS_NAMES.IS_EXPANDED);
      captions.setAttribute(SELECTORS.ARIA_EXPANDED, 'false');
      captions.removeEventListener(EVENTS.TRANSITIONEND, onTransitionEnd);
    };

    captions.addEventListener(EVENTS.TRANSITIONEND, onTransitionEnd);

    return this;
  }
}
